import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';
import type { KeyboardEvent, ReactNode, ThHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

/**
 * Tableau à défilement horizontal propre : c'est indispensable pour l'écran
 * Commandes, qui reprend les dix-sept colonnes de sa feuille Excel.
 */
export function Table({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div className="w-full overflow-x-auto">
      <table className={cn('w-full border-collapse text-sm', className)}>{children}</table>
    </div>
  );
}

export function THead({ children }: { children: ReactNode }) {
  return <thead className="border-b border-line bg-surface-sunken">{children}</thead>;
}

export function TBody({ children }: { children: ReactNode }) {
  return <tbody className="divide-y divide-line">{children}</tbody>;
}

/**
 * Ligne de tableau, cliquable quand `onClick` est fourni.
 *
 * Une ligne cliquable doit aussi s'ouvrir au clavier : sans cela, l'action
 * principale des écrans de liste — ouvrir une commande, une fiche, une facture —
 * est purement à la souris, et la tabulation saute les lignes sans les voir.
 * Elle devient donc un arrêt de tabulation qui répond à Entrée et à Espace,
 * avec un état de focus visible.
 *
 * Une grille complète (`role="grid"` et navigation aux flèches) serait plus
 * aboutie ; ici la sémantique de tableau est conservée telle quelle, et seule
 * l'accessibilité de l'action est rétablie.
 */
export function TR({
  children,
  className,
  onClick,
  alerte,
}: {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
  /** Met la ligne en évidence (stock faible, ligne à vérifier). */
  alerte?: boolean;
}) {
  const interactive = Boolean(onClick);

  function surTouche(e: KeyboardEvent<HTMLTableRowElement>) {
    /* Le focus peut être sur un lien ou un bouton de la ligne — la fiche client
     * en contient un. Laisser passer déclencherait deux fois l'action : celle de
     * l'élément, puis celle de la ligne. */
    if (e.target !== e.currentTarget) return;
    if (e.key !== 'Enter' && e.key !== ' ') return;

    // Espace ferait défiler la page sous la couche qui s'ouvre.
    e.preventDefault();
    onClick?.();
  }

  return (
    <tr
      onClick={onClick}
      onKeyDown={interactive ? surTouche : undefined}
      tabIndex={interactive ? 0 : undefined}
      className={cn(
        'transition-colors',
        alerte ? 'bg-mustard-50' : 'bg-white',
        interactive &&
          'cursor-pointer hover:bg-mustard-50 focus-visible:outline-none ' +
            'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-mustard-500',
        className,
      )}
    >
      {children}
    </tr>
  );
}

export function TH({
  children,
  className,
  numerique,
  ...props
}: ThHTMLAttributes<HTMLTableCellElement> & { numerique?: boolean }) {
  return (
    <th
      scope="col"
      className={cn(
        'whitespace-nowrap px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted',
        numerique && 'text-right',
        className,
      )}
      {...props}
    >
      {children}
    </th>
  );
}

export function TD({
  children,
  className,
  numerique,
  colSpan,
}: {
  children: ReactNode;
  className?: string;
  numerique?: boolean;
  colSpan?: number;
}) {
  return (
    <td
      colSpan={colSpan}
      className={cn('px-3 py-2 align-middle text-ink', numerique && 'text-right tabular', className)}
    >
      {children}
    </td>
  );
}

/** En-tête cliquable pilotant le tri serveur. */
export function THTri<T extends string>({
  children,
  colonne,
  triActuel,
  sens,
  onTrier,
  numerique,
}: {
  children: ReactNode;
  colonne: T;
  triActuel: T;
  sens: 'asc' | 'desc';
  onTrier: (colonne: T) => void;
  numerique?: boolean;
}) {
  const actif = triActuel === colonne;
  const Icone = actif ? (sens === 'asc' ? ChevronUp : ChevronDown) : ChevronsUpDown;

  return (
    <TH numerique={numerique}>
      <button
        type="button"
        onClick={() => onTrier(colonne)}
        className={cn(
          'inline-flex items-center gap-1 rounded transition-colors hover:text-ink',
          actif && 'text-ink',
          numerique && 'flex-row-reverse',
        )}
      >
        {children}
        <Icone aria-hidden className={cn('h-3.5 w-3.5', actif ? 'text-mustard-600' : 'opacity-50')} />
      </button>
    </TH>
  );
}
